import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button, type BadgeColor } from '@enamelai/design-system'
import stripeIcon from '@assets/images/stripe-icon.jpg'
import IntegrationCard from './stripe/IntegrationCard.component'
import { useStripeIntegration } from './stripe/useStripeIntegration'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import './IntegrationsStyle.css'
import Plaid from './plaid/Plaid.component'

type StripeStatusVariant = 'connected' | 'requires-action' | 'not-connected'

const statusConfig: Record<
  StripeStatusVariant,
  {
    label: string
    color: BadgeColor
    primaryButtonLabel: string
    showDashboard: boolean
  }
> = {
  connected: {
    label: 'Connected',
    color: 'vivid-green',
    primaryButtonLabel: 'Continue with onboarding',
    showDashboard: true,
  },
  'requires-action': {
    label: 'Requires action',
    color: 'orange',
    primaryButtonLabel: 'Continue with onboarding',
    showDashboard: true,
  },
  'not-connected': {
    label: 'Not connected',
    color: 'gray',
    primaryButtonLabel: 'Connect to Stripe',
    showDashboard: false,
  },
}

const Integrations = (): React.ReactElement => {
  const hooks = useStripeIntegration()
  const navigate = useNavigate()
  useSettingsSubpageLoading(!hooks.dataReady)

  const stripeVariant = useMemo((): StripeStatusVariant => {
    if (!hooks.hasMapping || !hooks.accountStatus) {
      return 'not-connected'
    }

    const isConnected =
      hooks.accountStatus.charges_enabled &&
      hooks.accountStatus.payouts_enabled &&
      hooks.accountStatus.onboarding_complete === true &&
      (hooks.accountStatus.requirements_due_count ?? 0) === 0

    return isConnected ? 'connected' : 'requires-action'
  }, [hooks.accountStatus, hooks.hasMapping])

  const stripeStatus = statusConfig[stripeVariant]

  const handleStripePrimaryAction = async (): Promise<void> => {
    if (!hooks.hasMapping) {
      const didProvision = await hooks.createOrConnectStripeAccount()
      if (!didProvision) return
    }
    void navigate('/settings/insurance-and-payments/accounting/stripe')
  }

  return (
    <div className="settings-integrations">
      <div className="settings-integrations__grid">
        <Plaid />
        <IntegrationCard
          name="Stripe"
          icon={stripeIcon}
          statusLabel={stripeStatus.label}
          statusColor={stripeStatus.color}
        >
          <Button
            className="settings-ds-button"
            variant="primary"
            size="md"
            type="button"
            onClick={() => void handleStripePrimaryAction()}
          >
            {stripeStatus.primaryButtonLabel}
          </Button>
          {stripeStatus.showDashboard ? (
            <Button
              className="settings-ds-button"
              variant="ghost"
              size="md"
              type="button"
              onClick={() => void hooks.openStripeDashboard()}
            >
              Stripe dashboard
            </Button>
          ) : null}
        </IntegrationCard>
      </div>
      {hooks.error ? (
        <p className="integration-card-error" role="alert">
          {hooks.error}
        </p>
      ) : null}
    </div>
  )
}

export default Integrations
